import { CODE_SYSTEMS, LOINC_CODES } from '@liza/fhir-utils';
import type { PlanDefinition, PlanDefinitionAction, Timing } from '@medplum/fhirtypes';
import { every, itemKind, loincCode, patient, taskCategory } from './builders';

export const MASLD_ONGOING_MEASUREMENTS_URL = 'https://liza.health/fhir/PlanDefinition/masld-ongoing-measurements';
export const MASLD_ONGOING_MEASUREMENTS_VERSION = '1.0.0';
/** Version-pinned canonical, the value an expanded suggestion records as its provenance. */
export const MASLD_ONGOING_MEASUREMENTS_CANONICAL = `${MASLD_ONGOING_MEASUREMENTS_URL}|${MASLD_ONGOING_MEASUREMENTS_VERSION}`;

/** AASLD numbers its positions as guidance statements rather than recommendations. */
const AASLD_2023 = {
  title: 'AASLD Practice Guidance on the Clinical Assessment and Management of MASLD (2023)',
  url: 'https://www.aasld.org/practice-guidelines',
} as const;

/**
 * The LOINC codes this protocol tracks — the three labs FIB-4 is calculated from.
 *
 * FIB-4 itself is a *calculated index*, not an orderable test: it is derived from age plus these
 * three values. So the protocol schedules the draw that feeds it rather than the index, and the three
 * share one cadence because they are one blood draw, not three independent results.
 */
export const MASLD_ONGOING_CODES = {
  AST: LOINC_CODES.AST,
  ALT: LOINC_CODES.ALT,
  PLATELETS: LOINC_CODES.PLATELETS,
  TOTAL_BILIRUBIN: '1975-2',
  ALBUMIN: '1751-7',
  INR: '6301-6',
} as const;

/**
 * `K76.0` is fatty liver not elsewhere classified, and `K75.81` is the MASH code; both are matched as
 * ICD-10 prefixes. The SNOMED code is the current MASLD concept — the nomenclature moved from
 * NAFLD/NASH to MASLD/MASH, so a chart may carry either generation of code.
 */
export const MASLD_FOCUS_CODINGS = [
  { system: CODE_SYSTEMS.ICD10_CM, code: 'K76.0', display: 'Fatty (change of) liver, not elsewhere classified' },
  { system: CODE_SYSTEMS.ICD10_CM, code: 'K75.81', display: 'Metabolic dysfunction-associated steatohepatitis' },
  {
    system: CODE_SYSTEMS.SNOMED,
    code: '1231824009',
    display: 'Metabolic dysfunction-associated steatotic liver disease',
  },
] as const;

/**
 * Every action is a patient-owned lab on the FIB-4 cadence. The interval is the guidance's shorter
 * end: AASLD repeats primary risk assessment every 1-2 years for anyone with type 2 diabetes,
 * prediabetes or two or more metabolic risk factors, which is the population a MASLD diagnosis sits
 * in.
 */
const measurement = (
  id: string,
  title: string,
  description: string,
  code: string,
  /** Omitted where AASLD asks for the result without naming an interval — see the liver panel below. */
  timing: Timing | undefined,
  locus: string,
): PlanDefinitionAction => ({
  id,
  title,
  description,
  code: loincCode(code),
  participant: patient,
  extension: [itemKind('measurement'), taskCategory('measurement')],
  ...(timing ? { timingTiming: timing } : {}),
  documentation: [{ type: 'citation', display: `${AASLD_2023.title}, ${locus}`, url: AASLD_2023.url }],
});

/** One of the three labs FIB-4 is calculated from, drawn on the index's cadence. */
const fib4Component = (id: string, title: string, code: string): PlanDefinitionAction =>
  measurement(
    id,
    title,
    'Drawn yearly, with AST and platelets, to recalculate FIB-4.',
    code,
    every(1, 'a'),
    'Guidance Statement 12',
  );

// --- actions ------------------------------------------------------------------------------------------

const ast = fib4Component('ast', 'AST', MASLD_ONGOING_CODES.AST);
const alt = fib4Component('alt', 'ALT', MASLD_ONGOING_CODES.ALT);
const platelets = fib4Component('platelets', 'Platelets', MASLD_ONGOING_CODES.PLATELETS);

/**
 * No cadence. AASLD attaches no interval to any of these, but they are the results a clinician reads
 * when liver synthetic function is the question, so they belong on the plan without a schedule
 * asserted on the guidance's behalf.
 */
const asNeededLiverPanel = (id: string, title: string, code: string): PlanDefinitionAction =>
  measurement(
    id,
    title,
    `${title}, as needed, where liver synthetic function is in question.`,
    code,
    undefined,
    'clinical assessment',
  );

const bilirubin = asNeededLiverPanel('bilirubin', 'Total bilirubin', MASLD_ONGOING_CODES.TOTAL_BILIRUBIN);
const albumin = asNeededLiverPanel('albumin', 'Albumin', MASLD_ONGOING_CODES.ALBUMIN);
const inr = asNeededLiverPanel('inr', 'INR', MASLD_ONGOING_CODES.INR);

// --- the protocol -------------------------------------------------------------------------------------

/**
 * What AASLD asks for on an ongoing basis in MASLD.
 *
 * The shape here is unusual and worth reading before changing: **AASLD gives no cadence for any of
 * these labs individually.** What it schedules is FIB-4, and FIB-4 is calculated from age, AST, ALT
 * and platelets. So the three labs inherit the index's interval, and giving each its own would model
 * one yearly blood draw as three unrelated results.
 *
 * Bilirubin, albumin and INR carry no cadence at all — AASLD attaches no interval to any of them — so
 * they are as-needed: on the plan, with no schedule asserted. Aminotransferases in isolation are
 * explicitly cautioned against (Guidance Statement 15 — they are frequently normal in advanced
 * disease), which is another reason the FIB-4 three are scheduled together as one index rather than
 * read individually.
 *
 * Two escalations are deliberately not modelled. A FIB-4 at or above 1.3 triggers second-tier testing
 * — elastography or an ELF panel, then hepatology referral — which is a different action in response
 * to a result, not a repeat interval. And cirrhosis brings six-monthly HCC surveillance by ultrasound,
 * which is imaging gated on a diagnosis this protocol does not match.
 *
 * One caveat on the threshold: 1.3 is not age-universal. AASLD notes 2.0 above age 65 and poor
 * accuracy below 35, so any future conditional action on the value needs the patient's age, not a
 * fixed cut-off.
 */
export const masldOngoingMeasurements: PlanDefinition = {
  resourceType: 'PlanDefinition',
  url: MASLD_ONGOING_MEASUREMENTS_URL,
  version: MASLD_ONGOING_MEASUREMENTS_VERSION,
  identifier: [{ system: 'urn:ietf:rfc:3986', value: MASLD_ONGOING_MEASUREMENTS_URL }],
  name: 'masld-ongoing-measurements',
  title: 'MASLD — ongoing measurements',
  status: 'active',
  experimental: true,
  type: {
    coding: [
      {
        system: 'http://terminology.hl7.org/CodeSystem/plan-definition-type',
        code: 'clinical-protocol',
        display: 'Clinical Protocol',
      },
    ],
  },
  date: '2026-09-15',
  publisher: 'Liza Health',
  description:
    'Results recommended for ongoing monitoring in metabolic dysfunction-associated steatotic liver disease, expanded onto a care-plan topic when a MASLD Condition is linked to it.',
  useContext: [
    {
      code: { system: 'http://terminology.hl7.org/CodeSystem/usage-context-type', code: 'focus' },
      valueCodeableConcept: { coding: [...MASLD_FOCUS_CODINGS] },
    },
  ],
  relatedArtifact: [{ type: 'derived-from', display: AASLD_2023.title, url: AASLD_2023.url }],
  action: [
    {
      id: 'ongoing-measurements',
      title: 'Ongoing measurements',
      description: 'The labs FIB-4 is calculated from, drawn together on the FIB-4 cadence.',
      action: [ast, alt, platelets, bilirubin, albumin, inr],
    },
  ],
};
